import Image from 'next/image';

import { Box, Flex, Heading, List, ListItem } from '@chakra-ui/react';
import { CheckedIcon } from '@udacity/chakra-uds-icons';

import { Card } from '~/components/card';
import { ContentContainer } from '~/components/container';
import { Glyph } from '~/components/glyph';
import { Markdown } from '~/components/markdown';
import { GlyphTitleMarkdown } from '~/features/enterprise-resource/models/shared-models';

export type OutcomesSectionProps = {
  title: string;
  outcomes: Array<GlyphTitleMarkdown>;
};

export function OutcomesSection({ title, outcomes }: OutcomesSectionProps) {
  return (
    <Box
      as='section'
      bgGradient='linear(180deg, #F3F7FF, #E2EBFB)'
      overflow='hidden'
      position='relative'
      paddingY={{
        base: 10,
        md: 20,
      }}
    >
      <Box
        height='711px'
        inset={0}
        margin='0 auto'
        maxWidth='1440px'
        position='absolute'
        display={{
          base: 'none',
          md: 'block',
        }}
      >
        <BackgroundImage position='left' src='/images/partnerships/left.png' />
        <BackgroundImage position='right' src='/images/partnerships/right.png' />
      </Box>
      <ContentContainer position='relative'>
        <Heading as='h2' mb={10} size='marketing-5xl' textAlign='center'>
          {title}
        </Heading>
        <List display='flex' flexWrap='wrap' gap={6}>
          {outcomes.map((outcome, idx) => (
            <ListItem key={idx} display='flex' flex='1 1 548px'>
              <Card as='article' bg='rgba(255, 255, 255, 0.9)' p={6}>
                <Flex
                  gap='16px'
                  mb={10}
                  alignItems={{
                    md: 'center',
                  }}
                  flexDir={{
                    base: 'column',
                    md: 'row',
                  }}
                >
                  <Glyph height={48} name={outcome.glyphName} width={48} />
                  <Heading as='p' size='marketing-2xl'>
                    {outcome.title}
                  </Heading>
                </Flex>
                <Box>
                  <Markdown
                    markdownText={outcome.markdownContent}
                    components={{
                      li: ({ children }) => (
                        <Flex as='li' gap={2}>
                          <CheckedIcon color='blue' height='32px' width='32px' />
                          {children}
                        </Flex>
                      ),
                      ul: ({ children }) => (
                        <List as='ul' display='flex' flexDir='column' gap={6} m={0} p={0}>
                          {children}
                        </List>
                      ),
                    }}
                  />
                </Box>
              </Card>
            </ListItem>
          ))}
        </List>
      </ContentContainer>
    </Box>
  );
}

function BackgroundImage({ src, position }: { src: string; position: 'left' | 'right' }) {
  return (
    <Box
      left={position === 'left' ? 0 : 'unset'}
      position='absolute'
      right={position === 'right' ? 0 : 'unset'}
      top={0}
    >
      <Box height='711px' position='relative' width='333px'>
        <Image
          alt=''
          quality={100}
          sizes='333px'
          src={src}
          style={{
            objectFit: 'cover',
          }}
          fill
        />
      </Box>
    </Box>
  );
}
